import React from "react";
import {
  Combobox,
  Input,
  Option,
  Label,
  Checkbox,
  makeStyles,
  Text,
} from "@fluentui/react-components";
import { CoverageComponentProps } from "./types";

const useStyles = makeStyles({
  rightAlignedInput: {
    "& input": {
      textAlign: "right",
    },
  },
  container: {
    display: "flex",
    flexDirection: "column",
    gap: "16px",
  },
  row: {
    display: "grid",
    gridTemplateColumns: "repeat(auto-fit, minmax(250px, 1fr))",
    gap: "16px",
    "@media (min-width: 768px)": {
      gridTemplateColumns: "repeat(2, 1fr)",
    },
    "@media (min-width: 1024px)": {
      gridTemplateColumns: "repeat(3, 1fr)",
    },
  },
  field: {
    display: "flex",
    flexDirection: "column",
    gap: "4px",
  },
  checkboxGroup: {
    display: "flex",
    flexDirection: "column",
    gap: "8px",
    "@media (min-width: 1024px)": {
      gridColumn: "span 2",
    },
  },
  fullWidth: {
    "@media (min-width: 768px)": {
      gridColumn: "span 2",
    },
    "@media (min-width: 1024px)": {
      gridColumn: "span 3",
    },
  },
  premiumDisplay: {
    marginTop: "12px",
    padding: "8px",
    backgroundColor: "#f3f2f1",
    borderRadius: "4px",
  },
});

const AllRiskCoverage: React.FC<CoverageComponentProps<"allRisk">> = ({
  config,
  onConfigChange,
  premium,
  onPremiumChange,
  t,
  classes,
}) => {
  const localClasses = useStyles();

  // Format number with thousands separator and currency
  const formatCurrency = (value: number): string => {
    return value ? `${value.toLocaleString("cs-CZ")} Kč` : "";
  };

  return (
    <div className={localClasses.container}>
      <div className={localClasses.row}>
        <div className={localClasses.field}>
          <Label htmlFor="sumInsured" required>
            {t("ila.coverages.allRisk.sumInsuredLabel")}
          </Label>
          <Input
            id="sumInsured"
            type="number"
            min={0}
            value={config.sumInsured.toString()}
            onChange={(_, data) =>
              onConfigChange({
                sumInsured: parseInt(data.value || "0"),
              })
            }
            placeholder="500000"
            className={localClasses.rightAlignedInput}
          />
        </div>

        <div className={localClasses.field}>
          <Label htmlFor="deductible">
            {t("ila.coverages.allRisk.deductibleLabel")}
          </Label>
          <Combobox
            id="deductible"
            value={formatCurrency(config.deductible)}
            onOptionSelect={(_, data) =>
              onConfigChange({
                deductible: parseInt(data.optionValue || "0"),
              })
            }
            className={localClasses.rightAlignedInput}
          >
            <Option value="0">0 Kč</Option>
            <Option value="10000">10 000 Kč</Option>
            <Option value="25000">25 000 Kč</Option>
            <Option value="50000">50 000 Kč</Option>
          </Combobox>
        </div>

        <div className={localClasses.field}>
          <Label htmlFor="commercialDiscount">
            {t("ila.coverages.allRisk.commercialDiscountLabel")}
          </Label>
          <Input
            id="commercialDiscount"
            type="number"
            min={0}
            max={100}
            value={config.commercialDiscount.toString()}
            onChange={(_, data) =>
              onConfigChange({
                commercialDiscount: parseInt(data.value || "0"),
              })
            }
            placeholder="10"
            contentAfter="%"
            className={localClasses.rightAlignedInput}
          />
        </div>
      </div>

      <div className={localClasses.row}>
        <div className={localClasses.checkboxGroup}>
          <Label>{t("ila.coverages.allRisk.additionalCoverages")}</Label>
          <Checkbox
            checked={config.theft}
            onChange={(_, data) => onConfigChange({ theft: !!data.checked })}
            label={t("ila.coverages.allRisk.theft")}
          />
          <Checkbox
            checked={config.vandalism}
            onChange={(_, data) =>
              onConfigChange({ vandalism: !!data.checked })
            }
            label={t("ila.coverages.allRisk.vandalism")}
          />
          <Checkbox
            checked={config.naturalDisasters}
            onChange={(_, data) =>
              onConfigChange({ naturalDisasters: !!data.checked })
            }
            label={t("ila.coverages.allRisk.naturalDisasters")}
          />
          <Checkbox
            checked={config.glass}
            onChange={(_, data) => onConfigChange({ glass: !!data.checked })}
            label={t("ila.coverages.allRisk.glass")}
          />
        </div>

        <div className={localClasses.field}>
          <Label htmlFor="territorialScope">
            {t("ila.coverages.allRisk.territorialScopeLabel")}
          </Label>
          <Combobox
            id="territorialScope"
            value={config.territorialScope}
            onOptionSelect={(_, data) =>
              onConfigChange({
                territorialScope: data.optionValue || "CZ",
              })
            }
          >
            <Option value="CZ">Czech Republic</Option>
            <Option value="EU">European Union</Option>
            <Option value="WORLD">Worldwide</Option>
          </Combobox>
        </div>
      </div>

      <div className={localClasses.row}>
        <div className={`${localClasses.field} ${localClasses.fullWidth}`}>
          <Label htmlFor="notes">{t("ila.coverages.allRisk.notesLabel")}</Label>
          <Input
            id="notes"
            type="text"
            value={config.notes || ""}
            onChange={(_, data) =>
              onConfigChange({
                notes: data.value || "",
              })
            }
            placeholder={t("ila.coverages.allRisk.notesPlaceholder")}
          />
        </div>
      </div>
    </div>
  );
};

export default AllRiskCoverage;
